import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import type { WaypointResponse, RouteResponse } from '@/shared/api/routes'
import { getDirections, type MapboxDirectionsError } from '../services/mapboxDirectionsService'
import { convertMapboxRouteToRouteResponse } from '../utils/routeResponseConverter'

export interface UseClientSideRoutesOptions {
  waypoints: WaypointResponse[]
  tripId: string | null
  profile?: 'driving' | 'walking' | 'cycling'
  enabled?: boolean
  accessToken: string | null
}

export interface UseClientSideRoutesReturn {
  routes: RouteResponse[]
  isLoading: boolean
  error: string | null
  regenerateRoutes: () => Promise<void>
}

// Debounce delay in milliseconds
const DEBOUNCE_DELAY_MS = 300

/**
 * Hook for generating routes client-side using Mapbox Directions API
 * 
 * Groups waypoints by trip_day, sorts by order_index, and generates routes
 * for each day with 2+ waypoints.
 */
export function useClientSideRoutes(
  options: UseClientSideRoutesOptions
): UseClientSideRoutesReturn {
  const {
    waypoints,
    tripId,
    profile = 'driving',
    enabled = true,
    accessToken
  } = options

  const [routes, setRoutes] = useState<RouteResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Request cancellation
  const abortControllerRef = useRef<AbortController | null>(null)
  
  // Cache for routes by waypoint signature
  const routeCacheRef = useRef<Map<string, RouteResponse[]>>(new Map())
  
  // Debounce timer
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Group waypoints by day and sort by order_index
  // Exclude POIs (is_poi = true) from route generation
  const waypointsByDay = useMemo(() => {
    if (!enabled || !tripId || waypoints.length === 0) {
      return new Map<number, WaypointResponse[]>()
    }

    const grouped = new Map<number, WaypointResponse[]>()

    waypoints.forEach(waypoint => {
      // Skip POIs - they don't generate routes
      if (waypoint.is_poi === true) {
        return
      }

      // Default to day 1 if trip_day is not set
      const day = waypoint.trip_day ?? 1
      
      if (!grouped.has(day)) {
        grouped.set(day, [])
      }
      
      grouped.get(day)!.push(waypoint)
    })

    // Sort waypoints within each day by order_index
    grouped.forEach((dayWaypoints, day) => {
      dayWaypoints.sort((a, b) => {
        const orderA = a.order_index ?? 999999
        const orderB = b.order_index ?? 999999
        return orderA - orderB
      })
    })

    return grouped
  }, [waypoints, tripId, enabled])

  // Generate cache key from waypoints
  const getCacheKey = useCallback((day: number, waypointIds: string[]): string => {
    return `${tripId}-${day}-${waypointIds.join(',')}`
  }, [tripId])

  // Generate routes for a single day
  const generateRouteForDay = useCallback(async (
    day: number,
    dayWaypoints: WaypointResponse[],
    signal: AbortSignal
  ): Promise<RouteResponse | null> => {
    // Filter waypoints with valid coordinates
    const validWaypoints = dayWaypoints.filter(
      wp => wp.latitude != null && wp.longitude != null
    )

    // Need at least 2 waypoints to generate a route
    if (validWaypoints.length < 2) {
      return null
    }

    // Check cache first
    const waypointIds = validWaypoints.map(wp => wp.id)
    const cacheKey = getCacheKey(day, waypointIds)
    const cachedRoutes = routeCacheRef.current.get(cacheKey)
    
    if (cachedRoutes && cachedRoutes.length > 0) {
      // Return cached route for this day
      return cachedRoutes.find(r => r.day === day) || null
    }

    // Extract coordinates as [lng, lat] pairs
    const coordinates: [number, number][] = validWaypoints.map(wp => [
      wp.longitude!,
      wp.latitude!
    ])

    // Call Mapbox Directions API
    try {
      if (!accessToken) {
        throw new Error('Mapbox access token is not available')
      }

      const directionsResponse = await getDirections({
        coordinates,
        profile,
        accessToken,
        signal
      })

      // Check if request was aborted
      if (signal.aborted) {
        return null
      }

      // Convert to RouteResponse
      const waypointNames = validWaypoints.map(wp => wp.name)
      const route = convertMapboxRouteToRouteResponse(
        directionsResponse.routes[0], // Use first route
        day,
        waypointIds,
        tripId!,
        waypointNames
      )

      // Cache the route
      routeCacheRef.current.set(cacheKey, [route])

      return route
    } catch (err) {
      // Handle errors gracefully
      if (err instanceof Error && err.name === 'AbortError') {
        // Request was cancelled, don't set error
        return null
      }

      const mapboxError = err as MapboxDirectionsError
      const errorMessage = mapboxError.message || 'Failed to generate route'
      
      
      // Don't throw - return null to skip this day
      // Error will be set at the top level if all days fail
      return null
    }
  }, [tripId, profile, accessToken, getCacheKey])

  // Generate all routes
  const generateRoutes = useCallback(async (force: boolean = false) => {
    if (!enabled || !tripId || !accessToken) {
      setRoutes([])
      setIsLoading(false)
      setError(null)
      return
    }

    // Cancel any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setIsLoading(true)
    setError(null)

    try {
      const routesPromises: Promise<RouteResponse | null>[] = []

      // Generate routes for each day
      waypointsByDay.forEach((dayWaypoints, day) => {
        routesPromises.push(
          generateRouteForDay(day, dayWaypoints, abortController.signal)
        )
      })

      // Wait for all routes to generate
      const generatedRoutes = await Promise.all(routesPromises)

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return
      }

      // Filter out null routes (days with < 2 waypoints or errors)
      const validRoutes = generatedRoutes.filter(
        (route): route is RouteResponse => route !== null
      )

      // Sort routes by day
      validRoutes.sort((a, b) => (a.day || 0) - (b.day || 0))

      setRoutes(validRoutes)

      // Set error if no routes were generated but we expected some
      if (validRoutes.length === 0 && waypointsByDay.size > 0) {
        // Check if any day had 2+ waypoints
        const hasValidDays = Array.from(waypointsByDay.values()).some(
          waypoints => waypoints.filter(wp => wp.latitude != null && wp.longitude != null).length >= 2
        )
        
        if (hasValidDays) {
          setError('Failed to generate routes. Please check your waypoints and try again.')
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Request was cancelled, don't set error
        return
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to generate routes'
      setError(errorMessage)
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false)
      }
    }
  }, [enabled, tripId, accessToken, waypointsByDay, generateRouteForDay])

  // Debounced route generation
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Don't generate routes if disabled or no waypoints
    if (!enabled || !tripId || waypoints.length === 0) {
      setRoutes([])
      setIsLoading(false)
      setError(null)
      return
    }

    // Debounce route generation
    debounceTimerRef.current = setTimeout(() => {
      generateRoutes()
    }, DEBOUNCE_DELAY_MS)

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [enabled, tripId, waypoints, generateRoutes])

  // Manual regeneration function
  const regenerateRoutes = useCallback(async () => {
    // Clear cache to force regeneration
    routeCacheRef.current.clear()
    await generateRoutes(true)
  }, [generateRoutes])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return {
    routes,
    isLoading,
    error,
    regenerateRoutes
  }
}

